import { Spinner } from "~/components/ui/spinner"

import { useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { FullScreen } from "~/components/screens/FullScreen"
import { authClient } from "~/lib/auth-client"

const nativeAuthProtocols = ["orbit:", "capacitor:", "ionic:"]

const parseAuthCallbackUrl = (input: string, fallback: string) => {
	if (input.startsWith("/") && !input.startsWith("//")) return input
	try {
		const url = new URL(input, window.location.origin)
		const configuredOrigin = import.meta.env.VITE_BASE_URL
			? new URL(import.meta.env.VITE_BASE_URL).origin
			: window.location.origin
		const isAllowedNativeUrl =
			nativeAuthProtocols.includes(url.protocol) &&
			url.username === "" &&
			url.password === ""
		const isAllowedWebUrl =
			(url.protocol === "http:" || url.protocol === "https:") &&
			url.username === "" &&
			url.password === "" &&
			(url.origin === window.location.origin || url.origin === configuredOrigin)
		if (isAllowedNativeUrl || isAllowedWebUrl) {
			return url.toString()
		}
	} catch {
		return fallback
	}
	return fallback
}

export default function NativeAuthPage() {
	const [searchParams] = useSearchParams()
	const provider = searchParams.get("provider")
	const email = searchParams.get("email")
	const callbackURL = parseAuthCallbackUrl(
		searchParams.get("callbackURL") ?? "orbit://auth/callback",
		"orbit://auth/callback"
	)
	const errorCallbackURL = parseAuthCallbackUrl(
		searchParams.get("errorCallbackURL") ??
			"orbit://auth/callback?redirect=%2Funauthorized",
		"orbit://auth/callback?redirect=%2Funauthorized"
	)
	const link = searchParams.get("link") === "1"

	useEffect(() => {
		const signIn = async () => {
			// Linking runs in this browser context instead of the native webview so
			// the OAuth state cookie round-trips with the provider callback.
			if (link && provider) {
				const result = await authClient.linkSocial({
					provider,
					callbackURL,
					errorCallbackURL,
					disableRedirect: true
				})
				if (result.error)
					throw new Error(result.error.message || "Account linking failed")
				if (!result.data?.url) throw new Error("No authorization URL returned")
				window.location.href = result.data.url
				return
			}

			if (
				provider === "discord" ||
				provider === "github" ||
				provider === "google"
			) {
				await authClient.signIn.social({
					provider,
					callbackURL,
					errorCallbackURL,
					disableRedirect: false
				})
				return
			}

			if (provider) {
				await authClient.signIn.sso({
					providerId: provider,
					loginHint: email ?? undefined,
					callbackURL,
					errorCallbackURL
				})
				return
			}

			window.location.href = errorCallbackURL
		}

		signIn().catch((error) => {
			console.error("Native auth launch failed", error)
			window.location.href = errorCallbackURL
		})
	}, [callbackURL, email, errorCallbackURL, provider, link])

	return (
		<FullScreen title="Please wait" centerContent={<Spinner size="lg" />} />
	)
}
